<script>
    import Copy from "@/public/components/common/copy.vue";
    import Preloader from "/public/components/common/uiKit/spinner/index.vue";
    import {mapGetters, mapMutations, mapState} from "vuex";
    import CurrencyIcon from "@/public/components/common/uiKit/CurrencyIcon.vue";
    import BigNumber from "bignumber.js";
    import {getNetworkSystemName} from "@/public/filters";
    import NetworkIcon from "@/public/components/common/uiKit/NetworkIcon.vue";
    import BaseButton from "@/public/components/common/uiKit/button/base/index.vue";
    export default {
        name: "InvoiceInfo",
        props: {
            statusEnum: {
                type: Object,
                required: true,
            },
        },
        components: {BaseButton, NetworkIcon, CurrencyIcon, Copy, Preloader},
        computed: {
            ...mapState('invoices', ['isLoading', 'invoice']),
            ...mapGetters('app', ['getCurrencyNameById']),
            rate () {
                if (!this.invoice.rate) {
                    return ''
                }
                return BigNumber(this.invoice.rate).toFixed(3)
            },
            currency () {
                if (!this.invoice.currencyId) {
                    return '-'
                }
                return this.getCurrencyNameById(this.invoice.currencyId)
            },
            receivedCurrency () {
                if (!this.invoice.receivedCurrency) {
                    return '-'
                }
                return this.getCurrencyNameById(this.invoice.receivedCurrency)
            },
            amount () {
                if (!this.invoice.amount) {
                    return '-'
                }
                let number = new BigNumber(this.invoice.amount).decimalPlaces(8);
                return number.toFixed();
            },
            id () {
                if (!this.invoice.id) {
                    return '-'
                }
              return this.invoice.id
            },
            orderAmount () {
                if (!this.invoice.orderAmount) {
                    return '-'
                }
                let number = new BigNumber(this.invoice.orderAmount).decimalPlaces(8);
                return number.toFixed();
            },
            receivedAmount () {
                if (!this.invoice.receivedCurrency && !Number(this.invoice.receivedAmount)  ) {
                    return '-'
                }
                let number = new BigNumber(this.invoice.receivedAmount).decimalPlaces(8);
                return number.toFixed();
            },
            receivedNetwork () {
                if (!this.invoice.receivedNetworkId) {
                    return ''
                }
                return getNetworkSystemName(this.invoice.receivedNetworkId)
            },
            orderId () {
                if (!this.invoice.orderId) {
                    return '-'
                }
                return this.invoice.orderId
            },
            status() {
                const {
                    statusEnum,
                    invoice,
                } = this;

                return statusEnum[invoice.status];
            },
        },
        methods: {
            ...mapMutations('invoices', ['setInvoiceInfoState', 'setInvoice']),
            closeInfo () {
                this.setInvoiceInfoState(false)
                this.setInvoice(null)
            },
            goToInvoice () {
                if (!this.invoice.invoiceLink) return

                window.open(this.invoice.invoiceLink, '_blank')
            }
        }
    }
</script>

<template>
<div class="info">
    <div class="info__bg" @click="closeInfo"></div>
    <div  class="info__content">
            <div class="info__close" @click="closeInfo">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.712087 0.712087C1.0782 0.345971 1.6718 0.345971 2.03791 0.712087L7 5.67418L11.9621 0.712087C12.3282 0.345971 12.9218 0.345971 13.2879 0.712087C13.654 1.0782 13.654 1.6718 13.2879 2.03791L8.32582 7L13.2879 11.9621C13.654 12.3282 13.654 12.9218 13.2879 13.2879C12.9218 13.654 12.3282 13.654 11.9621 13.2879L7 8.32582L2.03791 13.2879C1.6718 13.654 1.0782 13.654 0.712087 13.2879C0.345971 12.9218 0.345971 12.3282 0.712087 11.9621L5.67418 7L0.712087 2.03791C0.345971 1.6718 0.345971 1.0782 0.712087 0.712087Z" fill="var(--new-front-primary-font-color)"/>
                </svg>
            </div>
        <transition name="slide-fade" mode="out-in">
            <div v-if="!isLoading">
                    <div class="info__header">
                        <div class="info__header-topRow">
                            <div class="info__header-title">{{ $t('invoice.info_title') }}</div>
                            <div :class="['item__status', `item__status--${status.colorTag}`]">
                                {{ $t(status.title) }}
                            </div>
                        </div>
                        <div class="info__header-subtitle">№ {{ invoice.itemId }}</div>
                    </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            ID:
                        </div>
                        <div class="info__item-value">
                            {{ id | preparedString }}
                            <copy :value="invoice.id"/>
                        </div>
                    </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            {{ $t('invoice.date_of_creation') }}:
                        </div>
                        <div class="info__item-value">
                            {{ invoice.createdAt | getFormatDate('DD/MM/YYYY, HH:mm:ss') }}
                        </div>
                    </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            {{ $t('invoice.invoice_currency') }}:
                        </div>
                        <div v-if="currency" class="info__item-value">
                            <CurrencyIcon :currency="currency"/>
                            {{ currency }}
                        </div>
                        <div v-else class="info__item-value">
                            -
                        </div>
                    </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            {{ $t('invoice.info_sum') }}
                        </div>
                        <div class="info__item-value">
                            {{ amount }}
                        </div>
                    </div>
                <div class="info__item">
                    <div class="info__item-title">
                        {{ $t('invoice.info_rate') }}
                    </div>
                    <div class="info__item-value" v-if="rate">
                        <span>1 {{ invoice.receivedCurrencyName }}</span>
                        <svg class="info__rate-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M17.7099 1.06102C16.2496 -1.63937e-07 14.1664 -2.54996e-07 10 -4.37114e-07C5.83363 -6.19232e-07 3.75045 -7.10291e-07 2.29008 1.06102C1.81844 1.40368 1.40368 1.81844 1.06101 2.29008C-1.63937e-07 3.75045 -2.54996e-07 5.83363 -4.37114e-07 10C-6.19232e-07 14.1664 -7.10291e-07 16.2496 1.06101 17.7099C1.40368 18.1816 1.81844 18.5963 2.29008 18.939C3.75045 20 5.83363 20 10 20C14.1664 20 16.2496 20 17.7099 18.939C18.1816 18.5963 18.5963 18.1816 18.939 17.7099C20 16.2496 20 14.1664 20 10C20 5.83363 20 3.75045 18.939 2.29008C18.5963 1.81844 18.1816 1.40368 17.7099 1.06102ZM15.6682 12.1778C15.9838 12.4921 16.2824 12.8759 16.3638 13.387C16.3805 13.4919 16.3889 13.5977 16.3889 13.7038C16.3889 13.8099 16.3805 13.9157 16.3638 14.0206C16.2824 14.5316 15.9838 14.9155 15.6682 15.2298C15.3641 15.5328 14.9424 15.8672 14.4534 16.2549L13.1103 17.3197C12.7497 17.6057 12.2256 17.5451 11.9396 17.1845C11.6537 16.8239 11.7142 16.2997 12.0748 16.0138L13.3808 14.9783C13.5932 14.8099 13.7757 14.665 13.9328 14.5371L5.18518 14.5371C4.72494 14.5371 4.35185 14.164 4.35185 13.7038C4.35185 13.2436 4.72494 12.8705 5.18518 12.8705L13.9328 12.8705C13.7757 12.7426 13.5932 12.5977 13.3807 12.4293L12.0748 11.3938C11.7142 11.1079 11.6537 10.5837 11.9396 10.2231C12.2256 9.86246 12.7497 9.80191 13.1103 10.0879L14.4533 11.1527C14.9423 11.5404 15.3641 11.8748 15.6682 12.1778ZM14.815 7.12963C15.2752 7.12963 15.6483 6.75653 15.6483 6.2963C15.6483 5.83606 15.2752 5.46296 14.815 5.46296L6.06733 5.46296C6.22451 5.33514 6.40701 5.19019 6.61943 5.02176L7.92533 3.98631C8.28596 3.70037 8.34651 3.17622 8.06057 2.81559C7.77463 2.45496 7.25048 2.39441 6.88984 2.68035L5.54683 3.74521C5.05784 4.1329 4.63609 4.46726 4.33193 4.77025C4.01637 5.08459 3.71783 5.46845 3.6364 5.9795C3.6197 6.08435 3.61129 6.19021 3.61129 6.2963C3.61129 6.40238 3.6197 6.50824 3.6364 6.6131C3.71783 7.12415 4.01638 7.508 4.33194 7.82234C4.63609 8.12533 5.05783 8.45969 5.54681 8.84736L6.88984 9.91224C7.25048 10.1982 7.77463 10.1376 8.06057 9.777C8.34651 9.41637 8.28596 8.89222 7.92533 8.60628L6.61943 7.57084C6.40701 7.40241 6.22451 7.25745 6.06733 7.12963L14.815 7.12963Z" fill="var(--new-front-secondary-font-color)"/>
                        </svg>
                        <span>{{ rate }}</span>
                    </div>
                    <div class="info__item-value" v-else>-</div>
                </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            {{ $t('invoice.payment_currency') }}:
                        </div>
                        <div class="info__item-value" v-if="invoice.receivedCurrencyName">
                            <CurrencyIcon :currency="invoice.receivedCurrencyName"/>
                            {{ invoice.receivedCurrencyName }}
                        </div>
                        <div class="info__item-value" v-else>
                           -
                        </div>
                    </div>
                    <div class="info__item">
                        <div class="info__item-title">
                            {{ $t('invoice.payment_network') }}:
                        </div>
                        <div class="info__item-value">
                            <NetworkIcon :network="receivedNetwork"/>
                            {{ receivedNetwork | getNetworkFullNameByNetworkName }} <span v-if="receivedNetwork" class="info__item-value--secondary">{{ receivedNetwork | getNetworkShortNameByNetworkName }}</span>
                        </div>
                    </div>
                <div class="info__item">
                    <div class="info__item-title">
                        {{ $t('invoice.info_order_amount') }}
                    </div>
                    <div class="info__item-value">
                        {{ orderAmount }}
                    </div>
                </div>
                <div class="info__item">
                    <div class="info__item-title">
                        {{ $t('invoice.received_amount') }}:
                    </div>
                    <div v-if="receivedAmount" class="info__item-value">
                        {{ receivedAmount }}
                    </div>
                </div>
                <div class="info__item">
                    <div class="info__item-title">
                        {{ $t('invoice.order') }}:
                    </div>
                    <div class="info__item-value" v-if="invoice.orderId">
                        {{ orderId | preparedString }}
                        <copy v-if="invoice.orderId" :value="invoice.orderId" />
                    </div>
                    <div v-else class="info__item-value">
                        -
                    </div>
                </div>
                <div class="info__item">
                    <div class="info__item-title">
                        {{ $t('invoice.validity') }}:
                    </div>
                    <div v-if="invoice.orderExpiresAt" class="info__item-value">
                        {{ invoice.orderExpiresAt | getFormatDate('DD/MM/YYYY, HH:mm:ss') }}
                    </div>
                    <div v-else class="info__item-value">
                        -
                    </div>
                </div>
              <div class="info__item">
                <div class="info__item-title">
                  Webhook:
                </div>
                <div v-if="invoice.lastWebhookId"  class="info__item-value info__item-value--link">
                  <router-link :to="`/webhooks/${invoice.lastWebhookId}`" target='_blank'>{{ $t('invoice.goto') }} →</router-link>
                </div>
                <div v-else class="info__item-value">
                  -
                </div>
              </div>
                <div class="info__item" style="border-bottom: 0">
                    <div class="info__item-title">
                        {{ $t('invoice.info_description') }}:
                    </div>
                </div>
                <div class="info__item-value">
                    {{ invoice.itemDescription }}
                </div>
                <base-button class="info__btn" type="primary" :label="$t('invoice.info_btn_label')" :disabled="!invoice.invoiceLink" @click="goToInvoice" />
            </div>
            <preloader v-else />
        </transition>
    </div>
</div>
</template>

<style scoped lang="scss">
.info {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
    // Огромный z-index для перекрытия виджета интеркома
    z-index: 2147483001;
    &__rate-icon {
        margin: 0 10px;
    }
    &__btn {
        margin-top: 50px;
    }

    &__close {
        position: absolute;
        top: 30px;
        cursor: pointer;
    }
    &__bg {
        position: absolute;
        left: 0;
        top: 0;
        background: rgba(0, 0, 0, .50);
        height: 100vh;
        width: 100vw;
        z-index: 1;
        cursor: pointer;
    }
    &__content {
        width: 530px;
        height: 100vh;
        box-sizing: border-box;
        padding: 100px 50px 30px 50px;
        background: var(--new-front-main-bg);
        position: absolute;
        right: 0;
        z-index: 2;
        overflow: auto;
    }
    &__item {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 58px;
        border-bottom: 1px solid var(--new-front-divider-color);
        &:last-of-type {
            border-bottom: 0;
        }
        &-title {
            font-size: $h4;
            font-weight: 500;
            color: var(--new-front-secondary-font-color);
        }
        &-value {
            display: flex;
            align-items: center;
            gap: 5px;
            font-weight: 500;
            font-size: $h4;
            color: var(--new-front-primary-font-color);
          &--link {
            color: var(--new-front-purple);
            a {
              color: var(--new-front-purple);
            }
            &::v-deep {
              .copy-wrapper {
                margin-right: 5px !important;
              }
            }
          }
            &--secondary {
                color: var(--new-front-secondary-font-color);
            }
        }
    }
    &__header {
        margin-bottom: 20px;
        &-topRow {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        &-title {
            font-size: $h1;
            color: var(--new-front-primary-font-color)
        }
        &-subtitle {
            margin-top: 5px;
            font-size: $h4;
            color: var(--new-front-secondary-font-color)
        }
    }
}
.item__status {
    width: 120px;
    height: 32px;
    border-radius: 10px;
    text-align: center;
    padding: 5px 15px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    &--active {
        color: var(--new-front-status-text-active);
        background: var(--new-front-status-active);
    }
    &--created {
        color: var(--new-front-status-text-active);
        background: var(--new-front-status-active);
    }
    &--expired {
        color:  var(--new-front-status-text-expired);
        background: var(--new-front-status-expired);
    }
    &--completed {
        color: var(--new-front-status-text-processed);
        background: var(--new-front-status-processed);
    }
    &--overpaid {
        color: var(--new-front-status-text-processed);
        background: var(--new-front-status-processed);
    }
    &--in_processing {
        color: var(--new-front-status-text-pending);
        background: var(--new-front-status-pending);
    }
    &--partial_payment {
        color: var(--new-front-status-text-pending);
        background: var(--new-front-status-pending);
    }
    &--error {
        color: var(--new-front-status-text-error);
        background: var(--new-front-status-error);
    }
}
.slide-fade-enter-active {
    transition: all .3s ease;
}
.slide-fade-leave-active {
    transition: all .3s ease;
}
.slide-fade-enter, .slide-fade-leave-to
    /* .slide-fade-leave-active до версии 2.1.8 */ {
    transform: translateX(10px);
    opacity: 0;
}
::v-deep .copy-wrapper {
    margin: 0;
}
@media screen and (max-width: 576px) {
    .info__header-title {
        font-size: $h2;
    }
    .info__item-title {
        font-size: $h5;
    }
    .info__item-value {
        font-size: $h5;
    }
    .info__header-subtitle {
        font-size: $h5;
    }
    .info__content {
        width: 100%;
        height: 100vh;
        box-sizing: border-box;
        padding: 70px 20px 30px;
        overflow: auto;
    }
}
</style>
